import {
  AUSnackBarComponent,
  AUSnackBarConfig,
  IAUSnackBarData,
  AUSnackBarTypes
} from '@app/notifications/snackbars/au-snackbar.barrel';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, Subject, Subscription } from 'rxjs';
import { hasValue } from '@app/shared/utilities/comparison-helpers.utility';
import { take } from 'rxjs/operators';

/**
 * Service for displaying custom Material snackbars.
 *
 * Each `open` method displays a snackbar using our custom component
 * whenever `_currentSnackbarRef` is null. If a snackbar is already
 * active, the call is routed through `_updateSnackbarSubject`, which
 * emits an update event consumed by the custom component.
 *
 * The component maintains an internal list of snackbar data and renders
 * each entry in the template, creating the appearance of multiple
 * concurrent snackbars. Each emitted update appends a new snackbar
 * description to this list.
 */
@Injectable({ providedIn: 'root' })
export class AUSnackBarService {
  // private _cachedErrorData: ErrorCacheObject[] = [];
  private _currentSnackBarRef: MatSnackBarRef<AUSnackBarComponent>;
  private _sbDismissSubscription: Subscription;

  // subjects
  private _updateSnackbarSubject = new Subject<IAUSnackBarData>();

  private set currentSnackBarRef(value: MatSnackBarRef<AUSnackBarComponent>) {
    if (this._currentSnackBarRef || this._sbDismissSubscription) {
      this._sbDismissSubscription.unsubscribe();
      this._sbDismissSubscription = undefined;
    }

    this._currentSnackBarRef = value;

    // watch dismiss - necessary if dismissed from component
    this._sbDismissSubscription = this._currentSnackBarRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        this._currentSnackBarRef = undefined;
      });
  }

  get updateSnackbar$(): Observable<IAUSnackBarData> {
    return this._updateSnackbarSubject.asObservable();
  }

  constructor(private snackBar: MatSnackBar) {}

  public displayReadOnlyMsg() {
    const message = 'Form is in read-only mode';
    const config = new AUSnackBarConfig(AUSnackBarTypes.Info, '', message);
    config.setDuration(5_000);

    if (this._currentSnackBarRef) {
      this.update(config);
      return this._currentSnackBarRef;
    }

    const snackBarRef = this.snackBar.openFromComponent(AUSnackBarComponent, config.config);
    this.currentSnackBarRef = snackBarRef;

    return snackBarRef;
  }

  public open(snackBarConfig: AUSnackBarConfig, overwrite = true): MatSnackBarRef<any> {
    if (!overwrite) {
      if (hasValue(this._currentSnackBarRef)) {
        return;
      }
    }

    if (this._currentSnackBarRef) {
      this.update(snackBarConfig);
      return this._currentSnackBarRef;
    }

    const snackBarRef = this.snackBar.openFromComponent(AUSnackBarComponent, snackBarConfig.config);

    this.currentSnackBarRef = snackBarRef;

    return snackBarRef;
  }

  public openSimple(typeBool: boolean, message: string, timeout = undefined, servDesc?: string) {
    const sbType = typeBool ? AUSnackBarTypes.Success : AUSnackBarTypes.Error;
    const config = new AUSnackBarConfig(sbType, '', message, servDesc);

    if (hasValue(timeout) && timeout > 0) {
      config.setDuration(timeout);
    }

    if (this._currentSnackBarRef) {
      this.update(config);
      return this._currentSnackBarRef;
    }

    const snackBarRef = this.snackBar.openFromComponent(AUSnackBarComponent, config.config);
    this.currentSnackBarRef = snackBarRef;

    return snackBarRef;
  }

  public openError(
    message: string,
    servDesc?: string,
    timeout = undefined,
    expandMoreDetails = false
  ) {
    const config = new AUSnackBarConfig(AUSnackBarTypes.Error, '', message, servDesc);

    if (hasValue(timeout) && timeout > 0) {
      config.setDuration(timeout);
    }

    if (expandMoreDetails) {
      config.data.defaultMoreDetailsExpanded = true;
    }

    if (this._currentSnackBarRef) {
      this.update(config);
      return this._currentSnackBarRef;
    }

    const snackBarRef = this.snackBar.openFromComponent(AUSnackBarComponent, config.config);

    this.currentSnackBarRef = snackBarRef;

    return snackBarRef;
  }

  public openSimpleByType(
    type: AUSnackBarTypes,
    message: string,
    timeout?: number,
    servDesc?: string
  ) {
    const config = new AUSnackBarConfig(type, '', message, servDesc);

    if (timeout) {
      config.setDuration(timeout);
    }

    if (this._currentSnackBarRef) {
      this.update(config);
      return this._currentSnackBarRef;
    }

    const snackBarRef = this.snackBar.openFromComponent(AUSnackBarComponent, config.config);

    this.currentSnackBarRef = snackBarRef;

    return snackBarRef;
  }

  public update(snackBarConfig: AUSnackBarConfig): void {
    if (hasValue(this._currentSnackBarRef)) {
      this._updateSnackbarSubject.next(snackBarConfig.data);
    } else {
      this.open(snackBarConfig);
    }
  }

  public close(): void {
    const ref = this._currentSnackBarRef;
    // set undefined here instead of waiting for subscription to avoid timing issues
    this._currentSnackBarRef = undefined;
    // call dismiss to remove it from the UI
    ref?.dismiss();
  }
}
